import React, { useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  IconButton,
  Button,
  CircularProgress,
  Link as MuiLink,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormHelperText,
} from '@mui/material';
import HomeIcon from '@mui/icons-material/Home';
import FileInput from '../Shared/FileUploadPreview/FileInput';
import { useSnackBar } from '../FlowBuilder/Common/SnackBar/index.tsx';
import DynamicBreadcrumbs from '../Shared/Breadcrumb/index.tsx';
import { useNavigate } from 'react-router';
import { AiActions } from '../../Services/_actions/Ai/ai.actions.ts';
import CommonTable from '../Shared/Tables/TableV1.tsx';
import { convertToReadableFormat } from '../../utils/helper.jsx';
import {
  BLOCKED_EXTENSIONS,
  WHATSAPP_ALLOWED_MIME,
  WHATSAPP_LIMITS,
} from '../Whatsapp/Template/template.constant.js';

const MAX_AUDIO_SIZE = 20 * 1024 * 1024;

const AudioToText = () => {
  const [file, setFile] = useState<File | null>(null);
  const [leadId, setLeadId] = useState('');
  const [transcriptData, setTranscriptData] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [showTable, setShowTable] = useState(false);
  const [openModal, setOpenModal] = useState(false);
  const [modalContent, setModalContent] = useState('');

  const { showSnackBar } = useSnackBar();
  const navigate = useNavigate();
  const { convertAudioToText, getLeadTranscript } = AiActions();

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = event.target.files?.[0];
    if (!selectedFile) return;

    if (selectedFile.size > MAX_AUDIO_SIZE) {
      showSnackBar('Audio file must be under 20 MB.', 'error');
      return;
    }

    const validTypes = ['audio/mp3', 'audio/mpeg', 'audio/wav'];
    if (!validTypes.includes(selectedFile.type)) {
      showSnackBar('Invalid audio type. Accepted: MP3, MPEG, WAV.', 'error');
      return;
    }

    setFile(selectedFile);
  };

  const inferDocType = (mime: string): 'image' | 'video' | 'audio' | 'file' | null => {
    if (WHATSAPP_ALLOWED_MIME.image.includes(mime)) return 'image';
    if (WHATSAPP_ALLOWED_MIME.video.includes(mime)) return 'video';
    if (WHATSAPP_ALLOWED_MIME.audio.includes(mime)) return 'audio';
    if (WHATSAPP_ALLOWED_MIME.file.includes(mime)) return 'file';
    return null;
  };
  const bytesToMB = (bytes: number) => (bytes / (1024 * 1024)).toFixed(2);

  const handleUploadAndTranscript = async () => {
    if (!file || !leadId.trim()) {
      showSnackBar('Please select a file and enter a valid Lead ID.', 'error');
      return;
    }

    const fileName = file.name.toLowerCase();
    const ext = fileName.split('.').pop();
    if (BLOCKED_EXTENSIONS.has(ext)) {
      showSnackBar(`"${ext}" files are not allowed for security reasons.`, 'error');
      setFile(null);
      return;
    }

    const typeGroup = inferDocType(file.type);
    if (!typeGroup) {
      showSnackBar(`Unsupported file type (${file.type || ext}).`, 'error');
      setFile(null);
      return;
    }

    const limit = WHATSAPP_LIMITS[typeGroup];
    if (file.size > limit) {
      showSnackBar(
        `File too large for ${typeGroup}. Max ${bytesToMB(limit)} MB. Your file is ${bytesToMB(file.size)} MB.`,
        'error'
      );
      setFile(null);
      return;
    }

    setLoading(true);
    try {
      const dummyAudioLink = 'https://your-upload-url.com/audiofile.mp3';
      await convertAudioToText(file, leadId, dummyAudioLink);
      showSnackBar('Audio uploaded successfully.', 'success');
    } catch (err) {
      console.error(err);
      showSnackBar('Error during audio upload.', 'error');
    }
    setLoading(false);
  };

  const handleViewTranscript = async () => {
    if (!leadId.trim()) {
      showSnackBar('Lead ID is required to view transcript.', 'error');
      return;
    }

    setLoading(true);
    try {
      const response = await getLeadTranscript(leadId);
      console.log(response);

      const data = response || [];

      setTranscriptData(data);
      setShowTable(true);
      showSnackBar('Transcript fetched successfully.', 'success');
    } catch (err) {
      console.error(err);
      showSnackBar('Failed to fetch transcript data.', 'error');
    }
    setLoading(false);
  };

  const handleOpenModal = (text: string) => {
    setModalContent(text);
    setOpenModal(true);
  };

  const handleCloseModal = () => {
    setOpenModal(false);
  };

  const renderTextWithModal = (text: string) => {
    if (!text) return '—';
    return text.length > 20 ? (
      <>
        {text.slice(0, 20)}…{' '}
        <MuiLink
          component="button"
          onClick={() => handleOpenModal(text)}
          underline="none"
          sx={{ color: 'primary.main', cursor: 'pointer' }}
        >
          View
        </MuiLink>
      </>
    ) : (
      text
    );
  };

  const transcriptColumns = [
    {
      accessorFn: (_: any, index: number) => index + 1,
      header: 'Sr. No',
    },
    {
      accessorKey: 'audio_link',
      header: 'Recording',
      Cell: ({ cell }: any) => {
        const link = cell.getValue();
        return link ? (
          <MuiLink href={link} target="_blank" rel="noopener noreferrer">
            Listen
          </MuiLink>
        ) : (
          '—'
        );
      },
    },
    {
      accessorKey: 'transcript',
      header: 'Transcript',
      Cell: ({ cell }: any) => renderTextWithModal(cell.getValue()),
    },
    {
      accessorKey: 'summary',
      header: 'Summary',
      Cell: ({ cell }: any) => renderTextWithModal(cell.getValue()),
    },
    {
      accessorKey: 'created_at',
      header: 'Created At',
      Cell: ({ cell }: any) => convertToReadableFormat(cell.getValue()) || '—',
    },
    {
      accessorKey: 'updated_at',
      header: 'Updated At',
      Cell: ({ cell }: any) => convertToReadableFormat(cell.getValue()) || '—',
    },
    {
      accessorKey: 'updated_by',
      header: 'Updated By',
      Cell: ({ cell }: any) => cell.getValue() || '—',
    },
  ];

  const breadcrumbsData = [
    {
      label: (
        <IconButton color="primary">
          <HomeIcon />
        </IconButton>
      ),
      onClick: () => navigate('/'),
    },
    { label: 'Tools', onClick: () => navigate('/r/tools') },
    { label: 'Audio to Text' },
  ];

  return (
    <Box sx={{ backgroundColor: '#f8f9fa', borderRadius: '8px', padding: 2 }}>
      {/* <Grid container justifyContent="center" spacing={4}> */}
      {/* <Grid item xs={12} md={8}>
          <Card elevation={3}>
            <CardContent> */}
      <DynamicBreadcrumbs
        items={breadcrumbsData}
        onBreadcrumbClick={(e, item) => item?.onClick?.()}
      />
      <Typography variant="h6" gutterBottom>
        Upload Audio & Fetch Transcript
      </Typography>

      <Box mt={2}>
        <FileInput
          onFileSelect={handleFileChange}
          onFileRemove={() => setFile(null)}
          onFileUpload={handleUploadAndTranscript}
          file={file}
          accept="audio/mp3, audio/mpeg, audio/wav"
        />
        <FormHelperText sx={{ mt: 1 }}>Allowed: mp3, mpeg, wav • Max 20 MB</FormHelperText>
      </Box>

      <Box mt={2} sx={{ width: '309px' }}>
        <input
          type="text"
          placeholder="Enter Lead ID"
          value={leadId}
          onChange={(e) => setLeadId(e.target.value)}
          className="form-control"
        />
      </Box>

      <Box mt={2}>
        <Button variant="contained" onClick={handleUploadAndTranscript} disabled={loading}>
          {loading ? <CircularProgress size={20} /> : 'Upload Audio'}
        </Button>
      </Box>

      <Box mt={2}>
        <Button variant="outlined" onClick={handleViewTranscript} disabled={loading}>
          {loading ? <CircularProgress size={20} /> : 'View Response'}
        </Button>
      </Box>
      {/* </CardContent>
          </Card>
        </Grid> */}

      {showTable && (
        <Grid item xs={12}>
          <Typography variant="h6" gutterBottom>
            Transcript Results
          </Typography>
          <CommonTable columns={transcriptColumns} data={transcriptData} isLoading={loading} />
        </Grid>
      )}
      {/* </Grid> */}

      <Dialog open={openModal} onClose={handleCloseModal} fullWidth maxWidth="sm">
        <DialogTitle>Full Content</DialogTitle>
        <DialogContent dividers>
          <Typography variant="body1">{modalContent}</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseModal}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default AudioToText;
